import axios, { AxiosInstance } from "axios";
import API from "../Utils/API/API";
import workPolicyRoutes from "../routes/work-policy-routes";
import {
  DepartmentSearchResult,
  EmployeeSearchResult,
  WorkPolicyApi,
  WorkPolicyDetail,
  WorkPolicyFailureEnvelope,
  WorkPolicyListQuery,
  WorkPolicyRequest,
  WorkPolicyRetryRequest,
  WorkPolicyStage,
  WorkPolicySuccessEnvelope,
  WorkPolicySummary,
  WorkPolicyValidationIssue
} from "./work-policy-api.types";

export class WorkPolicyApiError extends Error {
  readonly code: string;
  readonly status?: number;
  readonly resourceId?: number;
  readonly lastCompletedStage?: WorkPolicyStage;
  readonly retryable: boolean;
  readonly fingerprint?: string;
  readonly issues?: WorkPolicyValidationIssue[];
  readonly networkUnknown: boolean;

  constructor(
    failure: WorkPolicyFailureEnvelope,
    status?: number,
    networkUnknown = false
  ) {
    super(failure.message);
    this.name = "WorkPolicyApiError";
    this.code = failure.code;
    this.status = status;
    this.resourceId = failure.resourceId;
    this.lastCompletedStage = failure.lastCompletedStage;
    this.retryable = failure.retryable;
    this.fingerprint = failure.fingerprint;
    this.issues = failure.issues;
    this.networkUnknown = networkUnknown;
  }
}

async function execute<T>(request: Promise<{ data: WorkPolicySuccessEnvelope<T> }>) {
  try {
    const response = await request;
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const failure = error.response.data as WorkPolicyFailureEnvelope;
      if (failure && failure.ok === false && typeof failure.code === "string") {
        throw new WorkPolicyApiError(failure, error.response.status);
      }
    }
    throw new WorkPolicyApiError(
      {
        ok: false,
        code: "WORK_POLICY_NETWORK_UNKNOWN",
        message: "서버 응답을 확인하지 못했습니다.",
        retryable: false
      },
      undefined,
      true
    );
  }
}

export function createWorkPolicyApi(client: AxiosInstance): WorkPolicyApi {
  return {
    list: (query: WorkPolicyListQuery) =>
      execute<WorkPolicySummary[]>(
        client.get(workPolicyRoutes.collection, { params: query })
      ),
    detail: (workPolicyId: number) =>
      execute<WorkPolicyDetail>(client.get(workPolicyRoutes.detail(workPolicyId))),
    create: (request: WorkPolicyRequest) =>
      execute<WorkPolicyDetail>(client.post(workPolicyRoutes.collection, request)),
    update: (workPolicyId: number, request: WorkPolicyRequest) =>
      execute<WorkPolicyDetail>(
        client.put(workPolicyRoutes.detail(workPolicyId), request)
      ),
    softDelete: (workPolicyId: number) =>
      execute<WorkPolicyDetail>(
        client.delete(workPolicyRoutes.detail(workPolicyId))
      ),
    retry: (workPolicyId: number, request: WorkPolicyRetryRequest) =>
      execute<WorkPolicyDetail>(
        client.post(workPolicyRoutes.retry(workPolicyId), request)
      ),
    searchDepartments: (keyword: string, limit = 30) =>
      execute<DepartmentSearchResult[]>(
        client.get(workPolicyRoutes.departments, {
          params: { keyword, limit: Math.min(limit, 30) }
        })
      ),
    searchEmployees: (keyword: string, limit = 30) =>
      execute<EmployeeSearchResult[]>(
        client.get(workPolicyRoutes.employees, {
          params: { keyword, limit: Math.min(limit, 30) }
        })
      )
  };
}

const workPolicyApi = createWorkPolicyApi(API());

export default workPolicyApi;
export * from "./work-policy-api.types";
